import React from 'react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import { getSubjectBySlug } from '../data/subjects';

interface SubjectPageProps {
  slug: string;
}

const SubjectPage: React.FC<SubjectPageProps> = ({ slug }) => {
  const subject = getSubjectBySlug(slug);

  if (!subject) {
    return (
      <div className="landing-page">
        <SiteNav currentPage="home" />
        <section className="page-hero">
          <div className="container">
            <h1 className="page-hero-title">Subject Not Found</h1>
          </div>
        </section>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="landing-page">
      <SiteNav currentPage="home" />

      {/* Hero */}
      <section className="page-hero" style={{ background: subject.gradient }}>
        <div className="container">
          <div className="subject-hero-icon" dangerouslySetInnerHTML={{ __html: subject.icon }} />
          <h1 className="page-hero-title">11+ {subject.name} Practice with AI Tutor</h1>
          <p className="page-hero-subtitle" style={{ fontStyle: 'normal' }}>{subject.tagline}</p>
          {!subject.comingSoon && (
            <div className="subject-hero-stats">
              <span className="subject-hero-badge">{subject.topics.length} topics</span>
              <span className="subject-hero-badge">{subject.totalQuestions.toLocaleString()}+ questions</span>
            </div>
          )}
          {subject.comingSoon && (
            <div className="subject-hero-stats">
              <span className="subject-hero-badge">Coming Soon</span>
            </div>
          )}
        </div>
      </section>

      {/* Topics Grid */}
      {!subject.comingSoon && subject.topics.length > 0 && (
        <section className="topics-section">
          <div className="container">
            <h2 className="section-title">What's Covered</h2>
            <p className="section-subtitle">{subject.description}</p>
            <div className="topic-grid">
              {subject.topics.map((topic) => (
                <div key={topic.slug} className="topic-card">
                  <div className="topic-card-header">
                    <h3>{topic.name}</h3>
                    <span className={`topic-badge ${subject.cssClass}`}>{topic.questions} Qs</span>
                  </div>
                  <p>{topic.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Coming Soon State */}
      {subject.comingSoon && (
        <section className="topics-section">
          <div className="container">
            <h2 className="section-title">Coming Soon</h2>
            <p className="section-subtitle">{subject.description}</p>
          </div>
        </section>
      )}

      {/* Why This Subject */}
      <section className="subject-why-section" style={{ background: subject.gradient }}>
        <div className="container">
          <div className="difference-content">
            <h2 className="section-title light">Why {subject.name} Matters</h2>
            <div className="difference-features">
              {subject.whyPoints.map((point, i) => (
                <div key={i} className="difference-feature">
                  <div className="feature-check">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </div>
                  <div>
                    <h4>{point.title}</h4>
                    <p>{point.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Content Blocks — SEO content */}
      {subject.contentBlocks && subject.contentBlocks.length > 0 && (
        <section className="subject-content-section">
          <div className="container">
            {subject.contentBlocks.map((block, i) => (
              <div key={i} className="subject-content-block">
                <h2>{block.heading}</h2>
                <p>{block.text}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Sample Question */}
      {subject.sampleQuestion && (
        <section className="sample-question-section">
          <div className="container">
            <h2 className="section-title">Try a Sample Question</h2>
            <p className="section-subtitle">Here's what a real ElevenUp question looks like</p>
            <div className="sample-question-card">
              <p className="sample-question-text">{subject.sampleQuestion.question}</p>
              <div className="sample-options">
                {subject.sampleQuestion.options.map((opt, i) => (
                  <div
                    key={i}
                    className={`sample-option ${opt.startsWith(subject.sampleQuestion!.correctAnswer + ')') ? 'correct' : ''}`}
                  >
                    {opt}
                  </div>
                ))}
              </div>
              <div className="sample-explanation">
                <strong>Answer: {subject.sampleQuestion.correctAnswer}</strong> &mdash; {subject.sampleQuestion.explanation}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <h2 className="section-title" style={{ color: 'white' }}>
            {subject.comingSoon
              ? 'Be the first to know when it launches'
              : `Start practising ${subject.name} today`}
          </h2>
          <p className="section-subtitle" style={{ color: 'rgba(255,255,255,0.8)' }}>
            Join early access &mdash; free to start, no credit card needed.
          </p>
          <a href="/#waitlist" className="btn-primary-light">
            Get Early Access
          </a>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default SubjectPage;
